import { Link } from 'react-router-dom';
import {
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader2,
  Search,
  ArrowRight,
  Trash2,
} from 'lucide-react';
import type { ImportBatch, ImportBatchStatus } from '../../types/import';

interface ImportBatchCardProps {
  batch: ImportBatch;
  onDelete?: (batchId: string) => void;
}

const STATUS_CONFIG: Record<
  ImportBatchStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  PENDING: {
    label: 'Pending',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    icon: <Clock className="h-4 w-4" />,
  },
  MATCHING: {
    label: 'Matching',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
  },
  REVIEW: {
    label: 'Ready for Review',
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    icon: <Search className="h-4 w-4" />,
  },
  IMPORTING: {
    label: 'Importing',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    icon: <CheckCircle className="h-4 w-4" />,
  },
  FAILED: {
    label: 'Failed',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    icon: <AlertCircle className="h-4 w-4" />,
  },
};

const SOURCE_LABELS: Record<string, string> = {
  CLELL_WADE: 'Clell Wade',
  BLAST_RAILS: 'Blast Rails',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getLinkPath(batch: ImportBatch): string {
  if (batch.status === 'REVIEW') return `/admin/imports/${batch.id}/review`;
  if (batch.status === 'COMPLETED') return `/admin/imports/${batch.id}/summary`;
  return `/admin/imports/${batch.id}/summary`;
}

/**
 * Displays a single import batch as a card
 */
export const ImportBatchCard = ({ batch, onDelete }: ImportBatchCardProps) => {
  const statusCfg = STATUS_CONFIG[batch.status];
  const canDelete = batch.status !== 'IMPORTING';

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete && window.confirm('Delete this import batch? This cannot be undone.')) {
      onDelete(batch.id);
    }
  };

  return (
    <Link
      to={getLinkPath(batch)}
      className="block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg mt-0.5">
            <FileSpreadsheet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {SOURCE_LABELS[batch.source] || batch.source}
              </span>
              <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                {statusCfg.icon}
                <span>{statusCfg.label}</span>
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {formatDate(batch.createdAt)}
              {batch.uploadedBy && (
                <span> by {batch.uploadedBy.displayName || batch.uploadedBy.email}</span>
              )}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {batch.fileNames.length} file{batch.fileNames.length !== 1 ? 's' : ''}
              {batch.fileNames.length <= 3 && `: ${batch.fileNames.join(', ')}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0 mt-1">
          {canDelete && onDelete && (
            <button
              onClick={handleDelete}
              className="p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
              title="Delete import"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <ArrowRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
      </div>

      {/* Progress bar for matching batches */}
      {(batch.status === 'MATCHING' || batch.status === 'PENDING') && batch.stats && batch.stats.matchingTotal > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Matching schools...</span>
            <span>
              {batch.stats.matchingProgress || 0} / {batch.stats.matchingTotal}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${Math.round(((batch.stats.matchingProgress || 0) / batch.stats.matchingTotal) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Progress bar for importing */}
      {batch.status === 'IMPORTING' && batch.stats && batch.stats.importTotal > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Importing people...</span>
            <span>
              {batch.stats.importProgress || 0} / {batch.stats.importTotal}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${Math.round(((batch.stats.importProgress || 0) / batch.stats.importTotal) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Stats row for completed batches */}
      {batch.status === 'COMPLETED' && batch.stats && (
        <div className="flex items-center space-x-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {batch.stats.personsCreated != null && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {batch.stats.personsCreated}
              </span>{' '}
              new people
            </span>
          )}
          {batch.stats.personsUpdated != null && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {batch.stats.personsUpdated}
              </span>{' '}
              updated
            </span>
          )}
          {batch.stats.orgsCreated != null && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {batch.stats.orgsCreated}
              </span>{' '}
              new orgs
            </span>
          )}
        </div>
      )}
    </Link>
  );
};
